import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert,
  CircularProgress, MenuItem, IconButton, Tooltip, Grid, InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import { useParams } from 'react-router-dom';
import { inventoryApiService, Product } from '../../services/inventoryService';
import { PageHeader, SectionCard, EmptyState, LoadingState, useConfirm } from '../../components/ui';
import { tokens } from '../../theme';
import { formatMoney, countLabel } from '../../utils/format';

const UNITS = ['szt', 'ml', 'g', 'l', 'kg'];
const unitLabel = (unit?: string) => (unit === 'szt' ? 'szt.' : unit || '');

const emptyForm = () => ({
  name: '', description: '', sku: '', unit: 'szt', category: '',
  sellingPrice: '', purchasePrice: '',
});

type FormField = keyof ReturnType<typeof emptyForm>;
type ApiIssue = { field?: string; message: string };

const FIELD_LABELS: Record<FormField, string> = {
  name: 'Nazwa produktu',
  description: 'Opis',
  sku: 'SKU / kod',
  unit: 'Jednostka',
  category: 'Kategoria',
  purchasePrice: 'Cena zakupu',
  sellingPrice: 'Cena sprzedaży',
};
const isFormField = (field?: string): field is FormField => !!field && field in FIELD_LABELS;

const numCell = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } as const;

export default function ProductsPage() {
  const { salonId } = useParams<{ salonId: string }>();
  const confirm = useConfirm();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [errorDetails, setErrorDetails] = useState<ApiIssue[]>([]);
  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState<Product | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await inventoryApiService.getProducts({ salonId });
      setProducts(data);
    } catch {
      setError('Nie udało się wczytać produktów. Odśwież stronę.');
    } finally {
      setLoading(false);
    }
  }, [salonId]);

  useEffect(() => { load(); }, [load]);

  const clearErrors = () => { setError(''); setErrorDetails([]); };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    clearErrors();
    setDialog(true);
  };

  const openEdit = (p: Product) => {
    setEditing(p);
    setForm({
      name: p.name,
      description: p.description || '',
      sku: p.sku || '',
      unit: p.unit,
      category: p.category || '',
      sellingPrice: p.sellingPrice?.toString() || '',
      purchasePrice: p.purchasePrice?.toString() || '',
    });
    clearErrors();
    setDialog(true);
  };

  const setField = (field: FormField, value: string) => {
    setForm(f => ({ ...f, [field]: value }));
    if (errorDetails.some(d => d.field === field)) setErrorDetails(list => list.filter(d => d.field !== field));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        sellingPrice: form.sellingPrice ? parseFloat(form.sellingPrice) : undefined,
        purchasePrice: form.purchasePrice ? parseFloat(form.purchasePrice) : undefined,
        salonId,
      };
      if (editing) {
        await inventoryApiService.updateProduct(editing.id, payload);
      } else {
        await inventoryApiService.createProduct(payload);
      }
      setDialog(false);
      clearErrors();
      load();
    } catch (e: any) {
      const details: ApiIssue[] = Array.isArray(e?.response?.data?.details) ? e.response.data.details : [];
      setErrorDetails(details);
      setError(details.length
        ? 'Nie udało się zapisać produktu — sprawdź dane w formularzu.'
        : e?.response?.data?.error || 'Nie udało się zapisać produktu. Spróbuj ponownie.');
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (id: string) => {
    const product = products.find(p => p.id === id);
    const ok = await confirm({
      title: 'Dezaktywować produkt?',
      details: product ? (
        <>
          <strong>{product.name}</strong>
          {(product.sku || product.category) && (
            <Box component="span" sx={{ display: 'block', color: tokens.muted, fontSize: '0.8125rem' }}>
              {[product.sku && `SKU ${product.sku}`, product.category].filter(Boolean).join(' · ')}
            </Box>
          )}
        </>
      ) : undefined,
      description: 'Produkt zniknie z katalogu i list wyboru. Dotychczasowe stany i historia magazynu zostaną zachowane.',
      confirmLabel: 'Dezaktywuj produkt',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await inventoryApiService.deleteProduct(id);
      load();
    } catch {
      setError('Nie udało się dezaktywować produktu. Spróbuj ponownie.');
    }
  };

  const filtered = products.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    (p.sku || '').toLowerCase().includes(search.toLowerCase()) ||
    (p.category || '').toLowerCase().includes(search.toLowerCase())
  );

  const fieldError = (field: FormField) => (dialog ? errorDetails.find(d => d.field === field)?.message : undefined);
  const otherIssues = errorDetails.filter(d => !isFormField(d.field));

  const errorAlert = error ? (
    <Alert severity="error" onClose={clearErrors} sx={{ mb: 2 }}>
      <Box>{error}</Box>
      {(dialog ? otherIssues : errorDetails).length > 0 && (
        <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
          {(dialog ? otherIssues : errorDetails).map((item, index) => (
            <li key={`${item.field || 'field'}-${index}`}>
              {isFormField(item.field) ? `${FIELD_LABELS[item.field]}: ` : ''}{item.message}
            </li>
          ))}
        </Box>
      )}
    </Alert>
  ) : null;

  return (
    <Box>
      <PageHeader
        breadcrumbs={[{ label: 'Usługi i magazyn' }]}
        title="Produkty"
        description={products.length
          ? `${countLabel(products.length, 'produkt', 'produkty', 'produktów')} w katalogu — kosmetyki używane w usługach i sprzedawane klientom.`
          : 'Kosmetyki używane w usługach i sprzedawane klientom.'}
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            Dodaj produkt
          </Button>
        }
      />

      {!dialog && errorAlert}

      {loading ? (
        <LoadingState variant="table" rows={5} />
      ) : products.length === 0 ? (
        <SectionCard>
          <EmptyState
            icon={<Inventory2OutlinedIcon />}
            title="Nie masz jeszcze produktów"
            description="Dodaj kosmetyki, które zużywasz w usługach lub sprzedajesz. Potem przyjmiesz je na stan magazynu."
            actions={<Button variant="outlined" startIcon={<AddIcon />} onClick={openCreate}>Dodaj produkt</Button>}
          />
        </SectionCard>
      ) : (
        <SectionCard padding="none">
          <Box sx={{ px: { xs: 2, md: 2.5 }, py: 1.5, borderBottom: `1px solid ${tokens.line}` }}>
            <TextField
              size="small" placeholder="Szukaj po nazwie, SKU lub kategorii" value={search}
              onChange={e => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              inputProps={{ 'aria-label': 'Szukaj produktu' }}
              sx={{ width: { xs: '100%', sm: 360 } }}
            />
          </Box>
          {filtered.length === 0 ? (
            <EmptyState
              compact
              icon={<SearchIcon />}
              title="Nic nie znaleziono"
              description={`Żaden produkt nie pasuje do „${search}”.`}
              actions={<Button variant="text" onClick={() => setSearch('')}>Wyczyść wyszukiwanie</Button>}
            />
          ) : (
            <TableContainer>
              <Table sx={{ minWidth: 860 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Nazwa</TableCell>
                    <TableCell>Kategoria</TableCell>
                    <TableCell>Jednostka</TableCell>
                    <TableCell align="right">Cena zakupu</TableCell>
                    <TableCell align="right">Cena sprzedaży</TableCell>
                    <TableCell>Używany w usługach</TableCell>
                    <TableCell align="right" aria-label="Akcje" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map(p => {
                    const usedIn = p.serviceConsumptions ?? [];
                    return (
                      <TableRow key={p.id} hover>
                        <TableCell sx={{ minWidth: 200 }}>
                          <Typography sx={{ fontWeight: 600, fontSize: 'inherit' }}>{p.name}</Typography>
                          {p.sku && (
                            <Typography variant="caption" component="div" sx={{ fontVariantNumeric: 'tabular-nums' }}>SKU {p.sku}</Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ color: p.category ? 'text.primary' : 'text.secondary' }}>{p.category || '—'}</TableCell>
                        <TableCell>{unitLabel(p.unit)}</TableCell>
                        <TableCell align="right" sx={numCell}>
                          {p.purchasePrice ? formatMoney(p.purchasePrice, { decimals: 2 }) : '—'}
                        </TableCell>
                        <TableCell align="right" sx={{ ...numCell, fontWeight: 600 }}>
                          {p.sellingPrice ? formatMoney(p.sellingPrice, { decimals: 2 }) : '—'}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 260 }}>
                          {usedIn.length > 0 ? (
                            <>
                              <Typography sx={{ fontWeight: 600, fontSize: 'inherit' }}>
                                {countLabel(usedIn.length, 'usługa', 'usługi', 'usług')}
                              </Typography>
                              <Typography variant="caption" component="div" noWrap title={usedIn.map(s => s.service.name).join(', ')}>
                                {usedIn.slice(0, 3).map(s => s.service.name).join(', ')}
                                {usedIn.length > 3 ? '…' : ''}
                              </Typography>
                            </>
                          ) : (
                            <Typography component="span" sx={{ color: 'text.secondary', fontSize: 'inherit' }}>—</Typography>
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap', py: 1 }}>
                          <Tooltip title="Edytuj produkt">
                            <IconButton size="small" aria-label={`Edytuj produkt ${p.name}`} onClick={() => openEdit(p)}>
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Dezaktywuj produkt">
                            <IconButton
                              size="small"
                              aria-label={`Dezaktywuj produkt ${p.name}`}
                              onClick={() => deactivate(p.id)}
                              sx={{ '&:hover': { color: 'error.main' } }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </SectionCard>
      )}

      <Dialog open={dialog} onClose={() => { setDialog(false); clearErrors(); }} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edytuj produkt' : 'Dodaj produkt'}</DialogTitle>
        <DialogContent>
          {dialog && errorAlert}
          <Grid container spacing={2} sx={{ pt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth required label="Nazwa produktu" value={form.name}
                onChange={e => setField('name', e.target.value)}
                error={!!fieldError('name')} helperText={fieldError('name')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Opis (opcjonalnie)" value={form.description} multiline rows={2}
                onChange={e => setField('description', e.target.value)}
                error={!!fieldError('description')} helperText={fieldError('description')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="SKU / kod" value={form.sku}
                onChange={e => setField('sku', e.target.value)}
                error={!!fieldError('sku')} helperText={fieldError('sku') || 'Kod producenta lub kod kreskowy'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select fullWidth label="Jednostka" value={form.unit}
                onChange={e => setField('unit', e.target.value)}
                error={!!fieldError('unit')} helperText={fieldError('unit') || 'W niej liczysz stan i zużycie'}
              >
                {UNITS.map(u => <MenuItem key={u} value={u}>{unitLabel(u)}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Kategoria" value={form.category} placeholder="np. Pielęgnacja włosów"
                onChange={e => setField('category', e.target.value)}
                error={!!fieldError('category')} helperText={fieldError('category')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Cena zakupu" type="number" value={form.purchasePrice}
                onChange={e => setField('purchasePrice', e.target.value)}
                inputProps={{ min: 0, step: 0.01 }}
                InputProps={{ endAdornment: <InputAdornment position="end">zł</InputAdornment> }}
                error={!!fieldError('purchasePrice')} helperText={fieldError('purchasePrice')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Cena sprzedaży" type="number" value={form.sellingPrice}
                onChange={e => setField('sellingPrice', e.target.value)}
                inputProps={{ min: 0, step: 0.01 }}
                InputProps={{ endAdornment: <InputAdornment position="end">zł</InputAdornment> }}
                error={!!fieldError('sellingPrice')} helperText={fieldError('sellingPrice')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => { setDialog(false); clearErrors(); }}>Anuluj</Button>
          <Button
            variant="contained" onClick={save} disabled={saving || !form.name}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {editing ? 'Zapisz zmiany' : 'Dodaj produkt'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
